import { TranslationsAlreadyUpToDateError } from "@core/i18n/translations-errors";
import { TranslationsService } from "@core/i18n/translations-service";
import { getLogger } from "@core/logging/logger";
import { Store } from "@core/storage/storage";
import { WordingKey } from "@typings/i18n-typings";
import { builtInWording } from "@wording/builtInWording";
import { wording } from "@wording/index";
import _ from "lodash";
import { Observable, observable } from "micro-observables";

export type LanguageTranslations = Record<WordingKey, string>;

export type Wordings = { [locale: string]: LanguageTranslations };

export interface Translations {
	translations: Wordings;
	versionDate: string;
}

export interface WeekdaysConfig {
	initials: Record<string, string[]>;
	firstDayOfWeek: Record<string, number>;
}

interface TranslationsStatus {
	available: boolean;
	error: unknown;
}


export class TranslationsManager {
	private readonly logger = getLogger("✍️ [TranslationManager]");
	private readonly _initialState: Translations = {
		translations: this.getBuiltInTranslations(),
		versionDate: "",
	};
	private readonly _translations = observable<Translations>(this._initialState);

	private readonly _translationsStatus = observable<TranslationsStatus>({ available: false, error: null });
	readonly translationsStatus = this._translationsStatus.readOnly();

	private readonly _weekdaysConfig = observable<WeekdaysConfig>({
		initials: {},
		firstDayOfWeek: {},
	});
	readonly weekdaysConfig = this._weekdaysConfig.readOnly();

	readonly availableLocales: Observable<string[]>;

	constructor(
		private readonly translationService: TranslationsService,
		private readonly store: Store<Translations>,
	) {
		this.availableLocales = this._translations.select(t => Object.keys(t.translations));
	}

	async init(): Promise<void> {
		this._translations.set(this.store.getItem(this._initialState));
		await this.updateWordingsOnVersion();
		await this.fetchWeekdaysConfig();
	}

	getLocaleWordings(locale: string): LanguageTranslations {
		const { translations } = this._translations.get();
		return locale in translations ? translations[locale] : translations.en;
	}

	resetWordingsVersionDate(): void {
		const newTranslations: Translations = { ...this._translations.get(), versionDate: "" };
		this._translations.set(newTranslations);
		this.store.setItem(newTranslations);
	}

	async updateWordingsOnVersion(forceUpdate?: boolean): Promise<void> {
		let error: unknown;
		let newVersion = "";
		const currentLocaleWordingsVersion = this._translations.get().versionDate;

		try {
			const { translations, versionDate } = await this.translationService.fetchWordings(
				forceUpdate ? "" : currentLocaleWordingsVersion,
			);
			this.logger.info("Fetching new wordings from date:", versionDate);
			newVersion = versionDate;
			this._translations.set({
				versionDate,
				translations: translations,
			});
			this.store.setItem({
				versionDate,
				translations: translations,
			});
		} catch (e) {
			if (e instanceof TranslationsAlreadyUpToDateError) {
				this.logger.info("Wordings up to date ! Last update date", currentLocaleWordingsVersion);
			} else {
				this.logger.error("Getting translations error:", e);
				error = e;
			}
			// backup startup: enable translations if an old version is stored locally
			newVersion = currentLocaleWordingsVersion;
		}
		this._translationsStatus.set({
			available: !_.isEmpty(newVersion),
			error,
		});
	}

	async fetchWeekdaysConfig(): Promise<void> {
		const weekdaysConfig = await this.translationService.fetchWeekdaysConfig();
		this._weekdaysConfig.set(weekdaysConfig);
	}

	debugGetTranslationsVersionDate(): Observable<string> {
		return this._translations.select(t => t.versionDate);
	}

	private getBuiltInTranslations(): Wordings {
		const base: Record<string, string> = wording.en;
		return Object.fromEntries(
			Object.entries(builtInWording).map(([key, value]) => {
				const override: Record<string, string> = value;
				return [key, { ...base, ...override } as LanguageTranslations];
			}),
		);
	}
}
